import type { Block, BlockNoteEditor } from "@blocknote/core";
import {
  hasFirebaseMediaToken,
  isFirebaseStorageUrl,
} from "@shared/lib/services/firebase.service";
import { preferTokenizedDomUrl, resolveImageDataUrl } from "./briefExportImageResolve";
import { collectBlocks } from "./editorBlocks";

export type MediaBlockInfo = {
  url: string;
  name?: string;
  caption?: string;
};

export { collectBlocks };

export function collectImageBlocks(editor: BlockNoteEditor): MediaBlockInfo[] {
  return collectBlocks(editor.document)
    .filter((block) => block.type === "image")
    .map((block) => {
      const props = block.props as {
        url?: string;
        name?: string;
        caption?: string;
      };
      return {
        url: props.url || "",
        name: props.name,
        caption: props.caption,
      };
    })
    .filter((item) => Boolean(item.url));
}

export function blockPlainText(block: Block): string {
  const content = (
    block as Block & {
      content?:
        | Array<{
            type?: string;
            text?: string;
            content?: Array<{ type?: string; text?: string }>;
          }>
        | string;
    }
  ).content;

  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";

  return content
    .map((item) => {
      if (typeof item === "string") return item;
      if (item?.type === "text") return item.text || "";
      if (item?.type === "link" && Array.isArray(item.content)) {
        return item.content
          .map((child) => (child?.type === "text" ? child.text || "" : ""))
          .join("");
      }
      return "";
    })
    .join("");
}

/** Copy tokenized DOM img URLs onto image blocks before export/save. */
export function syncTokenizedUrlsFromDom(editor: BlockNoteEditor): void {
  for (const block of collectBlocks(editor.document)) {
    if (block.type !== "image") continue;
    const props = block.props as { url?: string };
    const url = props.url;
    if (!url || !isFirebaseStorageUrl(url) || hasFirebaseMediaToken(url)) {
      continue;
    }
    const tokenized = preferTokenizedDomUrl(url);
    if (hasFirebaseMediaToken(tokenized) && tokenized !== url) {
      editor.updateBlock(block, {
        props: { ...props, url: tokenized, showPreview: true },
      });
    }
  }
}

/** Build DOC HTML with images inlined as data URLs when possible. */
export async function buildBriefHtmlWithMedia(
  editor: BlockNoteEditor
): Promise<string> {
  syncTokenizedUrlsFromDom(editor);
  const html = editor.blocksToFullHTML(editor.document);
  const imageBlocks = collectImageBlocks(editor);
  const imageUrlSet = new Set(imageBlocks.map((item) => item.url));

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="brief-export-root">${html}</div>`,
    "text/html"
  );
  const root = doc.getElementById("brief-export-root");
  if (!root) return html;

  root.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href") || "";
    if (!href || (!imageUrlSet.has(href) && !isFirebaseStorageUrl(href))) {
      return;
    }
    const img = doc.createElement("img");
    img.src = preferTokenizedDomUrl(href);
    img.alt = anchor.textContent?.trim() || "";
    img.setAttribute(
      "style",
      "max-width:100%;height:auto;display:block;margin:12px 0;"
    );
    anchor.replaceWith(img);
  });

  await Promise.all(
    [...root.querySelectorAll("img")].map(async (img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return;
      try {
        img.setAttribute("src", await resolveImageDataUrl(src));
      } catch {
        // keep remote URL
      }
    })
  );

  return root.innerHTML;
}
